'use client';

import { ExternalLink, Sparkles, Star, Quote, User2 } from 'lucide-react'
import { translations } from '@/lib/translations'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination, Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import { useEffect, useState } from 'react'
import { fetchList } from '@/lib/api'

export default function TestimonialsSection({ language }) {
  const t = translations[language]
  const [remote, setRemote] = useState(null)

  useEffect(() => {
    fetchList('/testimonials').then(setRemote).catch(() => setRemote(null))
  }, [])

  const testimonials = (remote && remote.length ? remote.map((row) => ({
    id: row.id,
    name: row.clientName,
    company: row.company,
    content: row.content,
    rating: row.rating || 5,
    role: row.position || 'Client',
  })) : [
    { 
      id: 1, 
      name: t.testimonials.person1, 
      company: t.testimonials.company1, 
      content: t.testimonials.text1, 
      rating: 5,
      role: 'CEO'
    },
    { 
      id: 2, 
      name: t.testimonials.person2, 
      company: t.testimonials.company2, 
      content: t.testimonials.text2, 
      rating: 5,
      role: 'Marketing Director'
    },
    { 
      id: 3, 
      name: t.testimonials.person3, 
      company: t.testimonials.company3, 
      content: t.testimonials.text3, 
      rating: 5,
      role: 'CTO'
    },
    { 
      id: 4, 
      name: t.testimonials.person4, 
      company: t.testimonials.company4, 
      content: t.testimonials.text4, 
      rating: 5,
      role: 'Founder'
    },
    { 
      id: 5, 
      name: t.testimonials.person5, 
      company: t.testimonials.company5, 
      content: t.testimonials.text5, 
      rating: 5,
      role: 'Product Manager'
    },
  ]);

  return (
    <section id="testimonials" className="px-4">
      <div className="max-w-7xl mx-auto py-2">
        <div className="text-center mb-4 md:mb-4">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-6 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary/80 uppercase tracking-wide">
              Client Stories
            </span>
          </div>

          {/* Title */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
              {t.testimonials.title}
            </span>
          </h2>

          {/* Animated Underline */}
          <div className="relative flex justify-center mb-6">
            <div className="w-24 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-full" />
            <div className="absolute w-12 h-1 bg-primary rounded-full animate-pulse" />
          </div>
        </div>

        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={24}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          breakpoints={{
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 3 },
          }}
          className="pb-14"
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide key={testimonial.id} className="h-full mb-8">
              <div className="group h-full rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/50 transition-all flex flex-col relative">
                {/* Decorative Quote Icon */}
                <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity z-10">
                  <Quote size={48} className="text-primary" />
                </div>

                {/* Content */}
                <div className="p-6 md:p-8 flex-grow flex flex-col relative z-10">
                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star 
                        key={i} 
                        className="w-5 h-5 fill-yellow-400 text-yellow-400" 
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-muted-foreground leading-relaxed mb-6 text-base md:text-lg flex-grow">
                    “{testimonial.content}”
                  </p>

                  {/* Client Info */}
                  <div className="border-t border-border/50 pt-5 mt-auto">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <User2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-bold text-foreground text-lg">
                          {testimonial.name}
                        </p>
                        <p className="text-xs text-primary/70 font-medium">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.company}
                    </p>
                  </div>
                </div>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 border-2 border-primary/0 rounded-2xl group-hover:border-primary/20 transition-all duration-500 pointer-events-none" />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}